import React, { useEffect, useState } from "react";
import {
  Alert,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import * as Clipboard from "expo-clipboard";
import * as Notifications from "expo-notifications";
import { Ionicons } from "@expo/vector-icons";
import axios from "axios";

import ScreenLayout from "@/Components/Layouts/Native/ScreenLayout";
import {
  getPushNotificationToken,
  setupNotificationAfterSigningIn,
} from "@/hooks/notifications";
import { type AppUserSession } from "@/schemas/auth/session";
import IdentityService from "@/services/user/identity";

type PushNotificationsScreenProps = {
  session: AppUserSession;
};

export const PushNotificationsScreen = ({
  session,
}: PushNotificationsScreenProps): React.JSX.Element => {
  const [deviceToken, setDeviceToken] = useState<string | null>(null);
  const [registeredToken, setRegisteredToken] = useState<string | null>(null);
  const [permissionState, setPermissionState] =
    useState<Notifications.PermissionStatus | null>(null);

  const loadData = async (): Promise<void> => {
    const permission = await Notifications.getPermissionsAsync();
    setPermissionState(permission.status);

    if (permission.status === "granted") {
      const expoPushToken = await getPushNotificationToken(false);
      setDeviceToken(expoPushToken);
    }

    const identityService = new IdentityService(session);
    const identity = await identityService.getOrCreateUserIdentity();
    if (identity.pushToken) {
      setRegisteredToken(identity.pushToken);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const requestPermissions = async (): Promise<void> => {
    setupNotificationAfterSigningIn(session);
  };

  const copyToClipboard = async (text: string | null): Promise<void> => {
    try {
      await Clipboard.setStringAsync(text || "");
      Alert.alert("Success", "Token copied to clipboard");
    } catch (error) {
      Alert.alert("Error", "Failed to copy token");
    }
  };

  const sendTestNotification = async (
    tokenStr: string | null,
  ): Promise<void> => {
    if (tokenStr === null) {
      return;
    }

    try {
      const message = {
        to: tokenStr,
        sound: "default",
        title: "Test Notification",
        body: "This is a test notification from Kai Social.",
      };

      const response = await axios.post(
        "https://exp.host/--/api/v2/push/send",
        message,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.status === 200) {
        Alert.alert("Success", "Test notification sent");
      } else {
        Alert.alert("Error", "Failed to send test notification");
      }
    } catch (error) {
      Alert.alert(
        "Error",
        "Failed to send test notification. Please try again.",
      );
    }
  };

  return (
    <ScreenLayout isLoading={false}>
      <ScrollView>
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Ionicons name="notifications-outline" size={24} color="#000" />
            <Text style={styles.sectionTitle}>Enable Push Notifications</Text>
          </View>
          <Text style={styles.description}>
            Stay updated with important information and updates from our app.
            You can disable notifications at any time in your settings.
          </Text>
          <TouchableOpacity
            style={[
              styles.button,
              permissionState ===
                ("granted" as Notifications.PermissionStatus) &&
                styles.buttonDisabled,
            ]}
            onPress={requestPermissions}
            disabled={permissionState === "granted"}
          >
            <Text style={styles.buttonText}>
              {permissionState === ("granted" as Notifications.PermissionStatus)
                ? "Notifications Enabled"
                : "Enable Notifications"}
            </Text>
          </TouchableOpacity>
        </View>

        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Ionicons name="phone-portrait-outline" size={24} color="#000" />
            <Text style={styles.sectionTitle}>Device Push Token</Text>
          </View>
          <Text style={styles.description}>
            Token generated by your device for receiving notifications.
          </Text>
          <View style={styles.tokenContainer}>
            <Text style={styles.tokenText} numberOfLines={1}>
              {deviceToken}
            </Text>
            <TouchableOpacity
              onPress={() => copyToClipboard(deviceToken)}
              disabled={deviceToken === null}
              style={styles.copyButton}
            >
              <Ionicons name="copy-outline" size={20} color="#666" />
            </TouchableOpacity>
          </View>
          <TouchableOpacity
            style={[
              styles.button,
              styles.testButton,
              deviceToken === null && styles.buttonDisabled,
            ]}
            onPress={() => sendTestNotification(deviceToken)}
            disabled={deviceToken === null}
          >
            <Text style={styles.buttonText}>Test Push Notification</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Ionicons name="person-outline" size={24} color="#000" />
            <Text style={styles.sectionTitle}>Account Push Token</Text>
          </View>
          <Text style={styles.description}>
            Token associated to your account for receiving notifications.
          </Text>
          <View style={styles.tokenContainer}>
            <Text style={styles.tokenText} numberOfLines={1}>
              {registeredToken}
            </Text>
            <TouchableOpacity
              onPress={() => copyToClipboard(registeredToken)}
              disabled={registeredToken === null}
              style={styles.copyButton}
            >
              <Ionicons name="copy-outline" size={20} color="#666" />
            </TouchableOpacity>
          </View>
          <TouchableOpacity
            style={[
              styles.button,
              styles.testButton,
              registeredToken === null && styles.buttonDisabled,
            ]}
            onPress={() => sendTestNotification(registeredToken)}
            disabled={registeredToken === null}
          >
            <Text style={styles.buttonText}>Test Push Notification</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </ScreenLayout>
  );
};

const styles = StyleSheet.create({
  section: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E5E5",
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginLeft: 12,
  },
  description: {
    fontSize: 14,
    color: "#666",
    marginBottom: 16,
    lineHeight: 20,
  },
  button: {
    backgroundColor: "#4299E1",
    padding: 16,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  buttonDisabled: {
    backgroundColor: "#A8A8A8",
  },
  buttonText: {
    color: "#FFF",
    fontSize: 16,
    fontWeight: "600",
  },
  testButton: {
    marginTop: 24,
    marginHorizontal: 16,
    backgroundColor: "#4299E1",
  },
  tokenContainer: {
    flexDirection: "row",
    backgroundColor: "#F5F5F5",
    padding: 12,
    borderRadius: 8,
    alignItems: "center",
  },
  tokenText: {
    flex: 1,
    fontSize: 12,
    fontFamily: Platform.OS === "ios" ? "Menlo" : "monospace",
    color: "#333",
  },
  copyButton: {
    padding: 8,
    marginLeft: 8,
  },
});

export default PushNotificationsScreen;
